import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "../App";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { toast } from "sonner";
import {
  TrendingUp, Users, DollarSign, Star, Target, ArrowUpRight, ArrowDownRight,
  Briefcase, CheckCircle2, Clock, Loader2, BarChart3, PieChart
} from "lucide-react";

export default function AnalyticsPage() {
  const { user } = useAuth();
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const token = localStorage.getItem("token");

  const fetchAnalytics = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/analytics/professional`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAnalytics(response.data);
    } catch (error) {
      toast.error("Failed to load analytics");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchAnalytics();
  }, [fetchAnalytics]);

  if (loading) {
    return (
      <div className="min-h-screen bg-sand flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-forest" />
      </div>
    );
  }

  const StatCard = ({ title, value, subtitle, icon: Icon, trend, color = "forest" }) => (
    <Card className="border-stone-200">
      <CardContent className="pt-6">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-sm text-gray-500 mb-1">{title}</p>
            <p className="text-3xl font-bold text-gray-900">{value}</p>
            {subtitle && (
              <p className="text-sm text-gray-500 mt-1">{subtitle}</p>
            )}
          </div>
          <div className={`w-12 h-12 rounded-xl bg-${color}/10 flex items-center justify-center`}>
            <Icon className={`w-6 h-6 text-${color}`} />
          </div>
        </div>
        {trend !== undefined && (
          <div className={`flex items-center gap-1 mt-3 text-sm ${trend >= 0 ? 'text-green-600' : 'text-red-600'}`}>
            {trend >= 0 ? <ArrowUpRight className="w-4 h-4" /> : <ArrowDownRight className="w-4 h-4" />}
            {Math.abs(trend)}% from last month
          </div>
        )}
      </CardContent>
    </Card>
  );

  return (
    <div className="min-h-screen bg-sand">
      <Header />
      
      <main className="pt-24 pb-12 px-4 md:px-8 max-w-7xl mx-auto" data-testid="analytics-page">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-heading font-bold text-2xl md:text-3xl text-gray-900 mb-2">
              Analytics Dashboard
            </h1>
            <p className="text-gray-600">Track your performance and grow your business</p>
          </div>
          <Link to="/dashboard">
            <Button variant="outline" className="rounded-full">
              Back to Dashboard
            </Button>
          </Link>
        </div>

        {analytics ? (
          <>
            {/* Top Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
              <StatCard
                title="Total Leads"
                value={analytics.leads.total}
                subtitle={`${analytics.leads.this_week} this week`}
                icon={Users}
                color="forest"
              />
              <StatCard
                title="Conversion Rate"
                value={`${analytics.bids.conversion_rate}%`}
                subtitle={`${analytics.bids.accepted} accepted`}
                icon={Target}
                color="forest"
              />
              <StatCard
                title="Total Revenue"
                value={`$${analytics.revenue.total.toLocaleString()}`}
                subtitle={`$${analytics.revenue.this_month} this month`}
                icon={DollarSign}
                color="forest"
              />
              <StatCard
                title="Average Rating"
                value={analytics.reviews.average_rating || "N/A"}
                subtitle={`${analytics.reviews.count} reviews`}
                icon={Star}
                color="forest"
              />
            </div>

            {/* Performance Cards */}
            <div className="grid md:grid-cols-2 gap-6 mb-8">
              {/* Lead Performance */}
              <Card className="border-stone-200">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <BarChart3 className="w-5 h-5 text-forest" />
                    Lead Performance
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="flex items-center justify-between p-3 bg-stone-50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                          <Users className="w-5 h-5 text-blue-600" />
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">Leads Received</p>
                          <p className="text-sm text-gray-500">This month</p>
                        </div>
                      </div>
                      <p className="text-2xl font-bold text-gray-900">{analytics.leads.this_month}</p>
                    </div>
                    
                    <div className="flex items-center justify-between p-3 bg-stone-50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                          <Briefcase className="w-5 h-5 text-green-600" />
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">Bids Submitted</p>
                          <p className="text-sm text-gray-500">Total</p>
                        </div>
                      </div>
                      <p className="text-2xl font-bold text-gray-900">{analytics.bids.total}</p>
                    </div>
                    
                    <div className="flex items-center justify-between p-3 bg-stone-50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center">
                          <CheckCircle2 className="w-5 h-5 text-purple-600" />
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">Jobs Completed</p>
                          <p className="text-sm text-gray-500">Total</p>
                        </div>
                      </div>
                      <p className="text-2xl font-bold text-gray-900">{analytics.jobs.completed}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Response Rate */}
              <Card className="border-stone-200">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <PieChart className="w-5 h-5 text-forest" />
                    Performance Metrics
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-6">
                    {/* Response Rate */}
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <p className="text-sm text-gray-600">Response Rate</p>
                        <p className="font-semibold text-gray-900">{analytics.performance.response_rate}%</p>
                      </div>
                      <div className="h-3 bg-gray-200 rounded-full overflow-hidden">
                        <div 
                          className="h-full bg-forest rounded-full transition-all duration-500"
                          style={{ width: `${Math.min(analytics.performance.response_rate, 100)}%` }}
                        />
                      </div>
                      <p className="text-xs text-gray-500 mt-1">
                        Percentage of leads you responded to
                      </p>
                    </div>

                    {/* Conversion Rate */}
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <p className="text-sm text-gray-600">Win Rate</p>
                        <p className="font-semibold text-gray-900">{analytics.bids.conversion_rate}%</p>
                      </div>
                      <div className="h-3 bg-gray-200 rounded-full overflow-hidden">
                        <div 
                          className="h-full bg-green-500 rounded-full transition-all duration-500"
                          style={{ width: `${Math.min(analytics.bids.conversion_rate, 100)}%` }}
                        />
                      </div>
                      <p className="text-xs text-gray-500 mt-1">
                        Percentage of bids accepted by customers
                      </p>
                    </div>

                    {/* Review Score */}
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <p className="text-sm text-gray-600">Customer Satisfaction</p>
                        <p className="font-semibold text-gray-900">
                          {analytics.reviews.average_rating ? `${analytics.reviews.average_rating}/5` : 'N/A'}
                        </p>
                      </div>
                      <div className="h-3 bg-gray-200 rounded-full overflow-hidden">
                        <div 
                          className="h-full bg-yellow-500 rounded-full transition-all duration-500"
                          style={{ width: `${(analytics.reviews.average_rating || 0) * 20}%` }}
                        />
                      </div>
                      <p className="text-xs text-gray-500 mt-1">
                        Based on {analytics.reviews.count} customer reviews
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Tips Card */}
            <Card className="border-forest/20 bg-forest/5">
              <CardContent className="py-6">
                <h3 className="font-semibold text-forest mb-3 flex items-center gap-2">
                  <TrendingUp className="w-5 h-5" />
                  Tips to Improve Performance
                </h3>
                <ul className="space-y-2 text-sm text-gray-700">
                  {analytics.performance.response_rate < 50 && (
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="w-4 h-4 text-forest mt-0.5 shrink-0" />
                      <span>Respond to more leads quickly - faster responses win more jobs!</span>
                    </li>
                  )}
                  {analytics.bids.conversion_rate < 30 && (
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="w-4 h-4 text-forest mt-0.5 shrink-0" />
                      <span>Add portfolio photos to showcase your work and increase trust.</span>
                    </li>
                  )}
                  {analytics.reviews.count < 5 && (
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="w-4 h-4 text-forest mt-0.5 shrink-0" />
                      <span>Ask happy customers to leave reviews - more reviews = more jobs!</span>
                    </li>
                  )}
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-4 h-4 text-forest mt-0.5 shrink-0" />
                    <span>Keep your profile and pricing up to date for better matches.</span>
                  </li>
                </ul>
              </CardContent>
            </Card>
          </>
        ) : (
          <div className="text-center py-12">
            <BarChart3 className="w-16 h-16 mx-auto mb-4 text-gray-300" />
            <p className="text-gray-500">No analytics data available yet</p>
            <p className="text-sm text-gray-400 mt-1">Start responding to leads to see your performance</p>
          </div>
        )}
      </main>

      <Footer />
    </div>
  );
}
